import React, { useCallback, useMemo, useState } from "react";

import { CloseButton, Col, Row, Stack } from "react-bootstrap";

import { Divider } from "@aws-amplify/ui-react";
import classNames from "classnames";

import { PaymentStatuses, ResaleFragment } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { useIsPastReportPage } from "@components/detail_drawer/useIsPastReportPage";
import { useAuthenticationContext } from "@context/authentication";
import styles from "@pages/dashboard/DetailsDrawer.module.scss";
import { ExternalRoles, Role } from "@roles";
import { canEditDetails } from "@util/resale";

import { IconLink } from "../IconLink";
import { ResaleStatusDropDown } from "../ResaleStatusDropDown";
import { ProtectedComponent, useIsAuthUserAllowed } from "../permission_control/ProtectedComponent";
import { ResaleStatusBadge } from "../table/ResaleStatusBadge";

interface ResaleStatusProps {
  resale: ResaleFragment;
  refetch: () => void;
}

export const ResaleStatus: React.FC<ResaleStatusProps> = ({ resale, refetch }) => {
  const { auth } = useAuthenticationContext();
  const [isEditingResaleStatus, setIsEditingResaleStatus] = useState<boolean>(false);
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);

  const toggleEditing = useCallback(() => {
    if (resale.payment_status === PaymentStatuses.INVOICE_REQUEST_SENT) {
      setIsEditingResaleStatus(false);
    }
    setIsEditingResaleStatus(!isEditingResaleStatus);
  }, [isEditingResaleStatus, resale.payment_status]);

  const handleOnSuccess = useCallback(() => {
    refetch();
    setIsEditingResaleStatus(false);
  }, [refetch]);
  const isPastReport = useIsPastReportPage();

  const canEdit = useMemo(
    () => canEditDetails(resale.payment_status, resale.resale_status, isPastReport),
    [isPastReport, resale.payment_status, resale.resale_status],
  );

  return (
    <Stack gap={4} className="col-md-10">
      <Row className="align-items-center">
        <Col>
          <h5 className={classNames(styles["stack-label"])}>Resale status</h5>
        </Col>
        {/*Permit: only VA Admins able to Edit Resale Status and if payment_status is in editablePaymentStatuses list*/}
        {canEdit && (
          <ProtectedComponent requiredGroups={[Role.VisualArtistAdmin]} auth={auth}>
            <Col xs="auto">
              {isEditingResaleStatus ? (
                <CloseButton onClick={toggleEditing} />
              ) : (
                <IconLink to="#" src={PenWriteEditIcon} text="Edit" className="me-2" onClick={toggleEditing} />
              )}
            </Col>
          </ProtectedComponent>
        )}
      </Row>
      <Stack className="col-md-10">
        {resale.resale_status && (
          <Row>
            {isEditingResaleStatus ? (
              <Col xs={12}>
                <ResaleStatusDropDown
                  onSuccess={handleOnSuccess}
                  resaleId={resale.resale_id}
                  vendorId={resale.vendor_id}
                  initialStatus={resale.resale_status}
                  isResaleLinkedToAnArtist={Boolean(resale.artist_id)}
                  id={resale.resale_id.toString()}
                />
              </Col>
            ) : (
              <div className="w-auto">
                <ResaleStatusBadge
                  cellValue={resale.resale_status}
                  resaleStatusReason={resale.resale_status_reason}
                  displayReason
                  isExternalUser={isExternalUser}
                />
              </div>
            )}
          </Row>
        )}
      </Stack>

      <Row>
        <Divider size="small" />
      </Row>
    </Stack>
  );
};
